import React, { useState } from "react";
import { Trophy, CaretDown, CaretUp, FloppyDisk, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../lib/teleproApi";

/**
 * Carte « Mon objectif quotidien » — le télépro choisit lui-même le nombre
 * de nouveaux prospects qu'il veut recevoir chaque matin (entre 10 et 100).
 *
 * Pourquoi : tout le monde n'a pas la même disponibilité. Quelqu'un qui ne peut
 * faire que 20 appels/jour n'a pas besoin d'une liste de 60 (qui finirait
 * reportée le lendemain inutilement — même si le carry-over s'en occupe,
 * ça fait des listes pleines de carryover, moins motivantes).
 *
 * Note rassurante: même si tu te fixes 60, les non appelés sont reportés.
 * Tu n'es jamais "en retard".
 */
export default function TeleproDailyGoalCard({ me, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [goal, setGoal] = useState(me?.daily_goal ?? 60);
  const [busy, setBusy] = useState(false);

  const current = me?.daily_goal ?? 60;

  const save = async () => {
    const value = Math.max(10, Math.min(100, parseInt(goal, 10) || 60));
    setBusy(true);
    try {
      const r = await teleproApi.patch("/me/profile", { daily_goal: value });
      onUpdated && onUpdated(r.data.telepro);
      setEditing(false);
      toast.success(`Objectif quotidien réglé sur ${value} prospects`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-5 mb-6" data-testid="daily-goal-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-primary/10 text-primary grid place-items-center rounded-sm shrink-0">
            <Trophy size={18} weight="duotone"/>
          </div>
          <div>
            <h3 className="font-display font-semibold text-sm sm:text-base mb-0.5">Mon objectif quotidien</h3>
            <p className="text-xs text-muted-foreground">
              Combien de <b>nouveaux</b> prospects souhaites-tu recevoir chaque matin ?
            </p>
          </div>
        </div>
        {!editing && (
          <button
            onClick={() => { setGoal(current); setEditing(true); }}
            className="text-xs font-semibold text-primary hover:text-[#0f3d29] inline-flex items-center gap-1 px-3 py-1.5 border border-border hover:border-primary rounded-sm"
            data-testid="daily-goal-edit"
          >
            Modifier
          </button>
        )}
      </div>

      {!editing ? (
        <div className="flex items-baseline gap-2 mb-3">
          <span className="font-display text-3xl font-medium text-primary tabular-nums" data-testid="daily-goal-value">{current}</span>
          <span className="text-sm text-muted-foreground">prospects / jour</span>
        </div>
      ) : (
        <div className="space-y-3 mb-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setGoal((g) => Math.max(10, (parseInt(g, 10) || 60) - 10))}
              className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-accent"
              data-testid="daily-goal-down"
              aria-label="Diminuer"
            >
              <CaretDown size={14} weight="bold"/>
            </button>
            <input
              type="number"
              min={10}
              max={100}
              step={5}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="w-24 bg-background border border-border rounded-sm px-3 py-2 text-center font-display text-2xl font-medium focus:border-primary outline-none"
              data-testid="daily-goal-input"
            />
            <button
              type="button"
              onClick={() => setGoal((g) => Math.min(100, (parseInt(g, 10) || 60) + 10))}
              className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-accent"
              data-testid="daily-goal-up"
              aria-label="Augmenter"
            >
              <CaretUp size={14} weight="bold"/>
            </button>
            <span className="text-xs text-muted-foreground">prospects / jour</span>
          </div>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="w-full accent-primary"
            data-testid="daily-goal-slider"
          />
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="daily-goal-save"
            >
              <FloppyDisk size={14} weight="bold"/> {busy ? "…" : "Enregistrer"}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setGoal(current); }}
              className="text-xs text-muted-foreground hover:text-foreground px-2 py-2"
              data-testid="daily-goal-cancel"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="bg-accent/50 border-l-2 border-primary rounded-sm p-3 mt-2 flex items-start gap-2" data-testid="daily-goal-info">
        <Info size={14} weight="duotone" className="text-primary shrink-0 mt-0.5"/>
        <p className="text-[11px] sm:text-xs text-foreground/80 leading-relaxed">
          <b>Aucune pression.</b> Tu n&apos;es pas obligé(e) d&apos;appeler les {current} prospects chaque jour.
          {" "}Ce qui n&apos;est pas appelé est <b>automatiquement reporté au lendemain</b> en priorité, puis complété
          par de nouveaux prospects. Tu peux baisser à 10 minimum si ton emploi du temps est serré.
        </p>
      </div>
    </div>
  );
}
